import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControlLabel,
  IconButton,
  Stack,
  Switch,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import { observer } from "mobx-react";
import {
  PatientTherapistPreferenceDto,
  TherapistListType,
  UserDto,
} from "../../../api-client";
import { useTherapistListByPatient } from "../../../stores/Appointment/appointment.queries";
import {
  useCreateTherapistListEntry,
  useUpdateTherapistListEntry,
  useDeleteTherapistListEntry,
} from "../../../stores/Appointment/appointment.mutation";
import { useAllUsersBasic } from "../../../stores/User/user.queries";
import { useUsersByLocation } from "../../../stores/Calendar/calendar.queries";
import { useLocationId } from "../../../stores/User/User.hooks";
import { MobiliAvatar } from "../../../atoms/MobiliAvatar";

type LocalEntry = PatientTherapistPreferenceDto & { _key: string };

interface TherapistListProps {
  patientId: number;
  readOnly?: boolean;
}

const userName = (u?: UserDto): string => {
  if (!u) return "Unbekannt";
  const full = `${u.firstName ?? ""} ${u.lastName ?? ""}`.trim();
  return full || u.nickName || u.username || "Unbekannt";
};

const TherapistList: React.FC<TherapistListProps> = observer(({ patientId, readOnly }) => {
  const { data: therapistListData = [] } = useTherapistListByPatient(patientId, !!patientId);
  const { data: allUsers = [] } = useAllUsersBasic();
  const { mutate: createTherapistListEntry } = useCreateTherapistListEntry();
  const { mutate: updateTherapistListEntry } = useUpdateTherapistListEntry();
  const { mutate: deleteTherapistListEntry } = useDeleteTherapistListEntry();
  const currentLocationId = useLocationId();
  // Therapists at the current location (dropdown source).
  const { data: locationUsers = [] } = useUsersByLocation(
    currentLocationId,
    undefined,
    false,
    !!currentLocationId
  );

  const [entries, setEntries] = useState<LocalEntry[]>([]);
  const [selectedTherapist, setSelectedTherapist] = useState<UserDto | null>(null);
  const [newListType, setNewListType] = useState<TherapistListType>(
    TherapistListType.Whitelist
  );
  // Muss (hard) vs Kann (soft). true = Muss.
  const [newMandatory, setNewMandatory] = useState<boolean>(false);
  // Control that started the save (for its spinner).
  const [pendingKey, setPendingKey] = useState<string | null>(null);

  const keyCounterRef = useRef(0);
  const genKey = () => `tl-${++keyCounterRef.current}`;

  // Server data is the source of truth; re-seed on every refetch.
  useEffect(() => {
    setEntries((therapistListData || []).map((e) => ({ ...e, _key: e.id ?? genKey() })));
  }, [therapistListData]);

  // Exclude therapists already on the list.
  const usedTherapistIds = useMemo(
    () => new Set(entries.map((e) => e.therapistId)),
    [entries]
  );
  const therapistOptions = useMemo(() => {
    return locationUsers
      .filter((u) => u.roles?.some((role) => role.id === "therapist"))
      .filter((u) => u.id && !usedTherapistIds.has(u.id))
      .sort((a, b) => userName(a).localeCompare(userName(b)));
  }, [locationUsers, usedTherapistIds]);

  const userById = useMemo(() => {
    const map = new Map<string, UserDto>();
    for (const u of allUsers) if (u.id) map.set(u.id, u);
    return map;
  }, [allUsers]);

  // Create a new entry
  const handleAdd = () => {
    const therapistId = selectedTherapist?.id;
    if (!therapistId) return;
    setPendingKey("add");
    createTherapistListEntry(
      {
        patientId,
        entry: {
          patientId,
          therapistId,
          listType: newListType,
          isMandatory: newMandatory,
        } as PatientTherapistPreferenceDto,
      },
      {
        onSettled: () => setPendingKey(null),
      }
    );
    setSelectedTherapist(null);
    setNewListType(TherapistListType.Whitelist);
    setNewMandatory(false);
  };

  // Update a single existing entry (optimistic local update, then server call).
  const handleChangeType = (entry: LocalEntry, value: TherapistListType | null) => {
    if (!value || !entry.id) return;
    setEntries((prev) =>
      prev.map((e) => (e._key === entry._key ? { ...e, listType: value } : e))
    );
    setPendingKey(entry._key);
    const { _key, ...rest } = entry;
    updateTherapistListEntry(
      { id: entry.id, patientId, entry: { ...rest, listType: value, patientId } },
      {
        onSettled: () => setPendingKey(null),
      }
    );
  };

  const handleToggleMandatory = (entry: LocalEntry, checked: boolean) => {
    if (!entry.id) return;
    setEntries((prev) =>
      prev.map((e) => (e._key === entry._key ? { ...e, isMandatory: checked } : e))
    );
    setPendingKey(entry._key);
    const { _key, ...rest } = entry;
    updateTherapistListEntry(
      { id: entry.id, patientId, entry: { ...rest, isMandatory: checked, patientId } },
      {
        onSettled: () => setPendingKey(null),
      }
    );
  };

  const handleDelete = (entry: LocalEntry) => {
    // New (unsaved) row: just drop it locally.
    if (!entry.id) {
      setEntries(entries.filter((e) => e._key !== entry._key));
      return;
    }
    // Saved row: delete it via the dedicated delete endpoint.
    // Keep the row (with spinner) until the refetch removes it.
    setPendingKey(`del:${entry._key}`);
    deleteTherapistListEntry(
      { id: entry.id, patientId },
      {
        onSettled: () => setPendingKey(null),
      }
    );
  };

  const whitelist = entries.filter((e) => e.listType === TherapistListType.Whitelist);
  const blacklist = entries.filter((e) => e.listType === TherapistListType.Blacklist);

  const renderRow = (entry: LocalEntry) => {
    const therapist = entry.therapistId ? userById.get(entry.therapistId) : undefined;
    const updatedByName = entry.updatedById
      ? userName(userById.get(entry.updatedById))
      : null;
    return (
      <Box
        key={entry._key}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          py: 0.5,
          px: 1,
          borderRadius: "8px",
          "&:hover": { backgroundColor: "action.hover" },
        }}
      >
        <MobiliAvatar
          user={therapist}
          isTherapist
          sx={{ width: 28, height: 28, fontSize: "0.7rem" }}
        />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Tooltip
            title={updatedByName ? `zuletzt geändert von ${updatedByName}` : ""}
          >
            <Typography variant="body2" noWrap sx={{ display: "inline-block", maxWidth: "100%" }}>
              {userName(therapist)}
            </Typography>
          </Tooltip>
        </Box>
        {!readOnly && (
          <>
            <ToggleButtonGroup
              size="small"
              exclusive
              value={entry.listType}
              onChange={(_e, value) => handleChangeType(entry, value as TherapistListType)}
              sx={{ flexShrink: 0 }}
            >
              <ToggleButton value={TherapistListType.Whitelist} color="success" sx={{ py: 0.25, px: 1 }}>
                Gewünscht
              </ToggleButton>
              <ToggleButton value={TherapistListType.Blacklist} color="error" sx={{ py: 0.25, px: 1 }}>
                Nicht gewünscht
              </ToggleButton>
            </ToggleButtonGroup>
            <Tooltip
              title={
                entry.isMandatory
                  ? "Muss – verbindlich einhalten"
                  : "Kann – nach Möglichkeit berücksichtigen"
              }
            >
              <FormControlLabel
                sx={{ mr: 0, width: 78, flexShrink: 0 }}
                control={
                  <Switch
                    size="small"
                    checked={!!entry.isMandatory}
                    onChange={(_e, checked) => handleToggleMandatory(entry, checked)}
                  />
                }
                label={entry.isMandatory ? "Muss" : "Kann"}
              />
            </Tooltip>
            <Box
              sx={{
                width: 20,
                flexShrink: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              {pendingKey === entry._key && <CircularProgress size={16} />}
            </Box>
            <Tooltip title="Entfernen">
              <span>
                <IconButton
                  size="small"
                  onClick={() => handleDelete(entry)}
                  disabled={pendingKey === `del:${entry._key}`}
                >
                  {pendingKey === `del:${entry._key}` ? (
                    <CircularProgress size={16} />
                  ) : (
                    <DeleteIcon fontSize="small" />
                  )}
                </IconButton>
              </span>
            </Tooltip>
          </>
        )}
      </Box>
    );
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
      {!readOnly && (
        <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: "wrap", gap: 1 }}>
          <Autocomplete
            size="small"
            sx={{ minWidth: 220, flex: 1, backgroundColor: "transparent" }}
            options={therapistOptions}
            value={selectedTherapist}
            onChange={(_e, value) => setSelectedTherapist(value)}
            getOptionLabel={(option) => userName(option)}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            renderInput={(params) => (
              <TextField {...params} label="Therapeut:in hinzufügen" />
            )}
          />
          <ToggleButtonGroup
            size="small"
            exclusive
            value={newListType}
            onChange={(_e, value) => value && setNewListType(value as TherapistListType)}
          >
            <ToggleButton value={TherapistListType.Whitelist} color="success" sx={{ py: 0.5, px: 1 }}>
              Gewünscht
            </ToggleButton>
            <ToggleButton value={TherapistListType.Blacklist} color="error" sx={{ py: 0.5, px: 1 }}>
              Nicht gewünscht
            </ToggleButton>
          </ToggleButtonGroup>
          <Tooltip
            title={
              newMandatory
                ? "Muss – verbindlich einhalten"
                : "Kann – nach Möglichkeit berücksichtigen"
            }
          >
            <FormControlLabel
              sx={{ mr: 0, width: 78, flexShrink: 0 }}
              control={
                <Switch
                  size="small"
                  checked={newMandatory}
                  onChange={(_e, checked) => setNewMandatory(checked)}
                />
              }
              label={newMandatory ? "Muss" : "Kann"}
            />
          </Tooltip>
          <Button
            variant="contained"
            startIcon={
              pendingKey === "add" ? <CircularProgress size={16} color="inherit" /> : <AddIcon />
            }
            onClick={handleAdd}
            disabled={!selectedTherapist || pendingKey === "add"}
          >
            Hinzufügen
          </Button>
        </Stack>
      )}

      {entries.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          Keine Einträge. Gewünschte oder nicht gewünschte Therapeut:innen für diese:n Patient:in
          können hier hinterlegt werden.
        </Typography>
      ) : (
        <Stack spacing={1.5}>
          {whitelist.length > 0 && (
            <Box>
              <Chip
                label={`Gewünscht (${whitelist.length})`}
                color="success"
                size="small"
                sx={{ mb: 0.5, bgcolor: "success.main", color: "success.contrastText" }}
              />
              <Stack>{whitelist.map(renderRow)}</Stack>
            </Box>
          )}
          {blacklist.length > 0 && (
            <Box>
              <Chip
                label={`Nicht gewünscht (${blacklist.length})`}
                color="error"
                size="small"
                sx={{ mb: 0.5, bgcolor: "error.main", color: "error.contrastText" }}
              />
              <Stack>{blacklist.map(renderRow)}</Stack>
            </Box>
          )}
        </Stack>
      )}

    </Box>
  );
});

export default TherapistList;
